<template>
    <div id="settings-wrapper">
        <!-- Info (polite) -->
        <v-snackbar top :color="info.color" v-model="showInfo" role="status" aria-live="polite" aria-atomic="true">
            <v-icon aria-hidden="true">{{ info.icon }}</v-icon>
            <span class="font-weight-bold pl-2">{{ info.message }}</span>
        </v-snackbar>

        <!-- Download progress (polite) -->
        <v-snackbar width="300px" vertical top v-model="showDownloadProgress" :timeout="2000000" color="primary" role="status" aria-live="polite" aria-atomic="true">
            <div class="pb-0">
                <div class="download-label">
                    <span class="mx-auto">Generating export…</span>
                </div>
                <v-progress-linear
                    style="width: 310px"
                    label="Downloading"
                    color="white"
                    indeterminate
                    rounded
                    height="6"
                    role="progressbar"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-label="Downloading" />
            </div>
        </v-snackbar>

        <!-- Added Info (assertive for important feedback) -->
        <v-snackbar top v-model="showUpdateMessage" color="#16c96d" role="alert" aria-live="assertive" aria-atomic="true">
            <v-icon aria-hidden="true">mdi-check-circle-outline</v-icon>
            <span class="font-weight-bold pl-2">Selected keywords were added to your research.</span>
        </v-snackbar>

        <v-snackbar top v-model="showAddMessage" color="#16c96d" role="alert" aria-live="assertive" aria-atomic="true">
            <v-icon aria-hidden="true">mdi-check-circle-outline</v-icon>
            <span class="font-weight-bold pl-2">Selected keywords were saved as a new research.</span>
        </v-snackbar>

        <v-snackbar top v-model="showDeleteMessage" color="#16c96d" role="alert" aria-live="assertive" aria-atomic="true">
            <v-icon aria-hidden="true">mdi-check-circle-outline</v-icon>
            <span class="font-weight-bold pl-2">Selected keywords were deleted.</span>
        </v-snackbar>

        <!-- Different locations in clipboard -->
        <v-snackbar top color="#fcba03" v-model="showLocError" role="alert" aria-live="assertive" aria-atomic="true">
            <v-row no-gutters>
                <v-col cols="1">
                    <v-icon class="pl-3" aria-hidden="true">mdi-alert-circle-outline</v-icon>
                </v-col>
                <v-col>
                    <span class="font-weight-bold"> You have keywords in your clipboard with search volume data from a different location. Please delete these keywords first. </span>
                </v-col>
            </v-row>
        </v-snackbar>
        <!-- Accessible activator button -->
        <!-- Settings-Button + Menu -->
        <v-menu
            v-model="sidebarmenu"
            :close-on-content-click="false"
            bottom
            left
            content-class="v-settings"
            :close-on-click="!tour_running"
            nudge-left="8"
            offset-x
            origin="top right"
            transition="scale-transition"
            @input="
                (val) => {
                    if (!val) $nextTick(() => setTimeout(restoreFocus, 50))
                }
            ">
            <template v-slot:activator="{ on, attrs }">
                <v-btn
                    id="settings-activator"
                    ref="clipboardSideBtn"
                    class="clipboard-trigger"
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    role="button"
                    tabindex="0"
                    aria-haspopup="dialog"
                    :aria-expanded="sidebarmenu ? 'true' : 'false'"
                    aria-controls="clipboard-menu"
                    aria-label="Open clipboard"
                    @click.stop="captureFocus()"
                    @keydown.enter.prevent="
                        captureFocus()
                        sidebarmenu = true
                    "
                    @keydown.space.prevent="
                        captureFocus()
                        sidebarmenu = true
                    ">
                    <v-icon aria-hidden="true" size="36px" color="white">mdi-text-box-plus</v-icon>
                    <span class="sr-only">Open clipboard</span>
                </v-btn>
            </template>

            <!-- dein bestehender v-card Inhalt bleibt -->
            <v-card
                v-focus-trap="sidebarmenu"
                width="320"
                class="clipboard-card"
                id="clipboard-menu"
                role="dialog"
                aria-modal="false"
                aria-labelledby="clipboard-title"
                @keydown.esc.stop="
                    sidebarmenu = false
                    $nextTick(() => setTimeout(restoreFocus, 50))
                ">
                <div class="clipboard-card__header">
                    <div class="clipboard-card__title-wrap">
                        <v-icon small color="primary" class="mr-2" aria-hidden="true">mdi-clipboard-text-outline</v-icon>
                        <strong class="d-inline-block" id="clipboard-title">Clipboard</strong>
                    </div>
                    <v-chip x-small :color="keywords.length ? 'primary' : 'grey lighten-1'" text-color="white" class="clipboard-card__count">{{ keywords.length }}</v-chip>
                </div>
                <v-card-text v-if="keywords.length === 0">
                    <hr class="my-3" id="divider" />
                    <v-alert type="info" color="primary" outlined class="clipboard-empty-state">
                        <b class="infotext">
                            <p>You can add keywords to the clipboard from anywhere in the app.</p>
                            <p class="infotext">Use this button to insert keywords:</p>
                        </b>
                        <div>
                            <v-btn tabindex="-1" fab dark small color="grey" class="fabicon btnpadding" aria-label="Add keyword to clipboard" title="Add keyword(s) to clipboard">
                                <v-icon aria-hidden="true">mdi-text-box-plus</v-icon>
                            </v-btn>
                        </div>
                    </v-alert>
                </v-card-text>
                <v-card-text v-else>
                    <v-data-table
                        data-focus-first
                        id="clipboardtable"
                        class="clipboard-table"
                        v-bind="tblOptions"
                        :headers="headers"
                        :items="keywords"
                        height="inherit"
                        item-key="keyword"
                        :items-per-page="perpage"
                        dense
                        aria-label="Clipboard keywords table">
                        <template v-slot:item.keyword="{ item }">
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <div v-on="on" :title="item.keyword">{{ shortenKeyword(item.keyword) }}</div>
                                    <!-- add to collection -->
                                </template>
                                <span>{{ item.keyword }}</span>
                            </v-tooltip>
                        </template>
                        <template v-slot:item.avgsv="{ item }">
                            <v-chip small color="primary">{{ $helpers.formatNumber(item.avgsv) }}</v-chip>
                        </template>
                        <template v-slot:item.action="{ item }">
                            <v-btn icon small @click="deleteItem(item)" aria-label="Remove keyword from clipboard">
                                <v-icon aria-hidden="true">mdi-close</v-icon>
                            </v-btn>
                        </template>
                    </v-data-table>
                    <hr class="my-3" id="divider" />
                    <!-- action buttons -->
                    <div class="actionBtns">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    class="mr-5"
                                    dark
                                    fab
                                    color="primary"
                                    small
                                    @click="
                                        captureFocus()
                                        downloadDialog = true
                                    "
                                    aria-label="Download keywords">
                                    <v-icon aria-hidden="true">mdi-cloud-download</v-icon>
                                </v-btn>
                            </template>
                            <span>Download keywords</span>
                        </v-tooltip>
                        <!-- save new collection -->
                        <v-tooltip top v-if="$helpers.isActionAllowed(6, user_rights)">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    id="save_btn"
                                    class="mr-5"
                                    color="primary"
                                    small
                                    fab
                                    dark
                                    @click="
                                        captureFocus()
                                        dialog = true
                                    "
                                    aria-label="Save as new collection">
                                    <v-icon aria-hidden="true">mdi-content-save</v-icon>
                                </v-btn>
                            </template>
                            <span>Save new collection</span>
                        </v-tooltip>
                        <!-- add to collection -->
                        <v-tooltip top v-if="$helpers.isActionAllowed(7, user_rights)">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    class="mr-5"
                                    color="primary"
                                    fab
                                    small
                                    dark
                                    @blur="getExistingLists"
                                    @click="
                                        captureFocus()
                                        researchDialog = true
                                    "
                                    v-bind="attrs"
                                    v-on="on"
                                    aria-label="Add to existing collection">
                                    <v-icon aria-hidden="true">mdi-file-plus</v-icon>
                                </v-btn>
                            </template>
                            <span>Add to an existing collection</span>
                        </v-tooltip>
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    color="primary"
                                    fab
                                    small
                                    dark
                                    @blur="getExistingLists"
                                    @click="
                                        captureFocus()
                                        deleteQuestion = true
                                    "
                                    v-bind="attrs"
                                    v-on="on"
                                    aria-label="Delete all keywords">
                                    <v-icon aria-hidden="true">mdi-delete</v-icon>
                                </v-btn>
                            </template>
                            <span>Delete all keywords</span>
                        </v-tooltip>
                    </div>
                    <!--save dialog -->
                    <v-dialog v-model="dialog" scrollable max-width="350px" @keydown.esc.native.stop="dialog = false">
                        <!-- <template v-slot:activator="{ on }">
                  <v-btn color='primary' v-on="on" block>save new research</v-btn>
                </template> -->
                        <v-card class="save-dialog-card" aria-labelledby="save-collection-title" role="dialog" @keydown.esc.stop="dialog = false">
                            <div class="save-dialog__header">
                                <div class="save-dialog__title-wrap">
                                    <v-icon small color="primary" class="mr-2" aria-hidden="true">mdi-content-save-outline</v-icon>
                                    <strong id="save-collection-title" class="d-inline-block">Save Collection</strong>
                                </div>
                                <span class="save-dialog__subtitle">Create a new research collection</span>
                            </div>
                            <v-card-text id="savebody" class="save-dialog__body">
                                <v-container fluid grid-list-xl id="kwpform">
                                    <v-layout align-center wrap>
                                        <v-flex xs12 id="kwpformflex">
                                            <span class="existMessage" role="status" aria-live="polite">{{ existMessage }}</span>
                                            <v-text-field
                                                id="collection_name"
                                                ref="collectionName"
                                                label="Collection name"
                                                v-model="documentName"
                                                prepend-icon="mdi-file-document"
                                                @focus="disableExistMessage"
                                                :aria-invalid="!!existMessage" />
                                        </v-flex>

                                        <v-flex xs12 id="kwpformflex">
                                            <v-select
                                                v-model="selectedLocation"
                                                :items="searchvolumeFromItems"
                                                item-text="loc"
                                                :menu-props="{ maxHeight: 150 }"
                                                label="Country for search volume"
                                                prepend-icon="mdi-map-marker"
                                                return-object
                                                disabled />
                                        </v-flex>
                                    </v-layout>
                                </v-container>
                            </v-card-text>
                            <v-divider />
                            <v-card-actions class="save-dialog__actions">
                                <v-btn
                                    id="final_save_collecion"
                                    class="save-dialog__save-btn"
                                    color="primary"
                                    :loading="btndisabled"
                                    dark
                                    @click="prepareSaveResearch(keywords)"
                                    block
                                    aria-label="Save collection">
                                    Save collection
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                    <!-- Download Dialog -->
                    <v-dialog v-model="downloadDialog" max-width="350px" id="downloaddialog">
                        <v-card class="download-dialog-card" aria-labelledby="download-title" role="dialog" @keydown.esc.stop="downloadDialog = false">
                            <div class="download-dialog__header">
                                <div class="download-dialog__title-wrap">
                                    <v-icon small color="primary" class="mr-2" aria-hidden="true">mdi-cloud-download-outline</v-icon>
                                    <strong id="download-title" class="d-inline-block">Download Research</strong>
                                </div>
                                <span class="download-dialog__subtitle">Choose your export format</span>
                            </div>
                            <v-card-text class="download-dialog__body">
                                <v-select
                                    hide-details
                                    class="download-dialog__select"
                                    v-model="doctype"
                                    :items="doctypes"
                                    filled
                                    item-text="value"
                                    item-value="data"
                                    return-object
                                    :prepend-icon="doctype.icon"
                                    label="Select file format"
                                    attach />
                            </v-card-text>
                            <v-card-actions class="download-dialog__actions">
                                <v-btn class="download-dialog__download-btn" dark color="primary" block @click="download" aria-label="Start download">Download</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                    <!-- add to research dialog -->
                    <v-dialog v-model="researchDialog" max-width="350px" class="text-center">
                        <v-card class="research-dialog-card" aria-labelledby="add-to-research-title" role="dialog" @keydown.esc.stop="researchDialog = false">
                            <div class="research-dialog__header">
                                <div class="research-dialog__title-wrap">
                                    <v-icon small color="primary" class="mr-2" aria-hidden="true">mdi-file-plus-outline</v-icon>
                                    <strong id="add-to-research-title" class="d-inline-block">Add to research</strong>
                                </div>
                                <span class="research-dialog__subtitle">Choose a target collection</span>
                            </div>
                            <v-card-text class="research-dialog__body">
                                <v-select
                                    v-model="selectedList"
                                    :items="existLists"
                                    item-text="listname"
                                    label="Available researches"
                                    prepend-icon="mdi-format-list-bulleted"
                                    return-object
                                    class="research-dialog__select"
                                    id="addtocollection" />
                            </v-card-text>
                            <v-divider />
                            <v-card-actions class="research-dialog__actions">
                                <v-btn class="research-dialog__add-btn" color="primary" :disabled="btndisabled" dark @click="addToResearch" block aria-label="Add keywords to research">
                                    Add keywords to research
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                    <!-- Delete Dialog List -->
                    <v-dialog v-model="deleteQuestion" max-width="350px">
                        <v-card role="dialog" aria-labelledby="confirm-delete-title">
                            <v-card-title id="confirm-delete-title" class="text-center">Are you sure?</v-card-title>
                            <v-card-text class="text-center">
                                <v-btn class="mr-3" text absolute left @click="deleteQuestion = false" aria-label="Cancel deletion">Nevermind</v-btn>
                                <v-btn color="primary" text @click="deleteList()" aria-label="Confirm deletion">Yes</v-btn>
                            </v-card-text>
                        </v-card>
                    </v-dialog>
                    <!-- Delete Dialog Item -->
                    <!-- <v-dialog v-model="deleteQuestionItem" max-width="300">
            <v-card>
              <v-card-title style="{text-align: center}"> Are you sure?</v-card-title>
              <v-card-text class="text-center">
                <v-btn class="mr-3" text @click="deleteQuestionItem = false"> Nevermind </v-btn>
                <v-btn color="#42DEFA" text @click="deleteItem()"  >  Yes </v-btn>
              </v-card-text>
            </v-card>
          </v-dialog> -->
                    <!-- Keep Keywords in List Dialog -->
                    <v-dialog v-model="keepKeywordsDialog" max-width="350">
                        <v-card role="dialog" aria-labelledby="keep-clipboard-title">
                            <v-card-title id="keep-clipboard-title" class="text-center">Keep keywords in clipboard?</v-card-title>
                            <v-card-text class="text-center">
                                <v-btn
                                    class="mr-3"
                                    text
                                    @click="
                                        keywords = []
                                        keepKeywordsDialog = false
                                        $store.dispatch('SET_CLIPBOARD_HEADERS', [])
                                    "
                                    aria-label="Do not keep keywords">
                                    No
                                </v-btn>
                                <v-btn id="keppKeywordsBtn" color="primary" text @click="keepKeywordsDialog = false" aria-label="Keep keywords">Yes</v-btn>
                            </v-card-text>
                        </v-card>
                    </v-dialog>
                </v-card-text>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
    // Mixins
    import { onlyKeywords } from "./helper/utils"
    import Proxyable from "vuetify/lib/mixins/proxyable"
    import { mapMutations, mapState } from "vuex"
    import { isCollectionBusy } from "../utils/collectionBusy"
    // import DownloadDialog from "./DownloadDialog.vue";

    export default {
        name: "DashboardCoreSettings",
        // components: {
        //   DownloadDialog
        // },
        mixins: [Proxyable],

        data: () => ({
            // get selectedLocation() {
            //     if (localStorage.selectedLocation) {
            //         return JSON.parse(localStorage.selectedLocation)
            //     } else {
            //         return { loc: "Germany", value: "2276", cc: "de" }
            //     }
            // },
            // get keywords() {
            //     // console.log(localStorage)
            //     // if(localStorage.getItem('keywords') == "undefined"){
            //     //   localStorage.setItem("keywords",[]);
            //     // }
            //     if (localStorage.getItem("keywords") && localStorage.getItem("keywords") != "undefined") {
            //         if (localStorage.getItem("keywords") != "[object Object],[object Object]") {
            //             return JSON.parse(localStorage.getItem("keywords"))
            //         } else {
            //             return []
            //         }
            //     } else {
            //         return []
            //     }
            // },

            // set keywords(value) {
            //     localStorage.setItem("keywords", JSON.stringify(value))
            // },
            // get downloadheaders() {
            //   if (localStorage.getItem('downloadheaders')) {
            //     if (localStorage.getItem('downloadheaders') != "[object Object],[object Object]") {
            //       return JSON.parse(localStorage.getItem('downloadheaders'));
            //     }else{
            //       return[]
            //     }
            //   } else {
            //     return [];
            //   }
            // },
            collectionType: { value: "Research", data: "research" },
            types: [
                { value: "Research", data: "research" }
                // { value: "Rankings", data: "rankings"}
            ],
            doctypes: [
                {
                    value: "Microsoft Excel (.xlsx)",
                    data: "json2xls",
                    icon: "mdi-microsoft-excel"
                },
                {
                    value: "Comma separated values (.csv)",
                    data: "json2csv",
                    icon: "mdi-file-delimited"
                }
            ],
            doctype: {
                value: "Microsoft Excel (.xlsx)",
                data: "json2xls",
                icon: "mdi-microsoft-excel"
            },
            downloadDialog: false,
            tblOptions: {
                "footer-props": {
                    "items-per-page-options": [5, 10, 15]
                }
            },
            tblheight: 150,
            perpage: 10,
            switchlbl: "don´t publish",
            auth: "Basic " + window.btoa(process.env.VUE_APP_AUTH),
            apiUrl: process.env.VUE_APP_APIURL,
            documentName: "",
            // searchvolumeFrom: localStorage.selectedLocation ?  JSON.parse(localStorage.selectedLocation) : { loc: "Germany", value: "2276", cc:"de" },
            searchvolumeFromItems: [
                { loc: "France", value: "2250" },
                { loc: "Spain", value: "2724" },
                { loc: "Netherlands", value: "2528" },
                { loc: "Germany", value: "2276" },
                { loc: "Austria", value: "2040" },
                { loc: "Switzerland", value: "2756" },
                { loc: "United Kingdom", value: "2826" },
                { loc: "Italy", value: "2380" },
                { loc: "Poland", value: "2616" },
                { loc: "Russia", value: "2643" },
                { loc: "Croatia", value: "2191" },
                { loc: "Czechia", value: "2203" },
                { loc: "Slovakia", value: "2703" },
                { loc: "Slovenia", value: "2705" }
            ],
            publish: true,
            existMessage: "",
            existLists: [],
            selectedList: {},
            dialogm1: "",
            dialog: false,
            researchDialog: false,
            listkeywords: [],
            headers: [
                {
                    text: "Keyword",
                    align: "left",
                    sortable: true,
                    value: "keyword"
                },
                {
                    text: "Vol.",
                    value: "avgsv",
                    align: "left",
                    sortable: true
                },
                { text: "", value: "action", sortable: false }
            ],
            color: "#00CAE3",

            menu: false,
            keepKeywordsDialog: false,
            showAddMessage: false,
            showUpdateMessage: false,
            showDeleteMessage: false,
            deleteQuestion: false,
            deleteQuestionItem: false,
            selectedItem: [],
            showLocError: false,
            btndisabled: false,
            info: {
                message: null,
                icon: null,
                color: null
            },
            showInfo: false,
            showDownloadProgress: false,
            lastFocusedEl: null
        }),

        computed: {
            keywords: {
                // getter
                get: function () {
                    return this.$store.state.clipboard_keywords
                },
                // setter
                set: function (newValue) {
                    this.$store.dispatch("SET_CLIPBOARD_KEYWORDS", newValue)
                }
            },
            tour_running() {
                return this.$store.state.tour_running
            },
            ...mapState(["barImage"]),
            sidebarmenu: {
                get() {
                    return this.$store.state.sidebarMenu
                },
                set(val) {
                    this.$store.commit("SET_SIDEBARMENU", val)
                }
            },
            ...mapState(["user_rights"]),
            selectedLocation() {
                return this.$store.state.clipboard_location
            }
        },

        watch: {
            dialog(val) {
                if (val) {
                    this.$nextTick(() => {
                        const el = this.$refs.collectionName && (this.$refs.collectionName.$el || this.$refs.collectionName)
                        if (el && el.querySelector && el.querySelector("input")) {
                            el.querySelector("input").focus()
                        }
                    })
                } else {
                    // Dialog wurde geschlossen (auch via ESC) → Menü ggf. wieder öffnen
                    if (!this.$store.state.sidebarMenu) {
                        this.$nextTick(() => this.$store.commit("SET_SIDEBARMENU", true))
                    }
                    this.restoreFocus()
                }
            },
            researchDialog(val) {
                if (!val) {
                    if (!this.$store.state.sidebarMenu) {
                        this.$nextTick(() => this.$store.commit("SET_SIDEBARMENU", true))
                    }
                    this.restoreFocus()
                }
            },
            downloadDialog(val) {
                if (!val) {
                    if (!this.$store.state.sidebarMenu) {
                        this.$nextTick(() => this.$store.commit("SET_SIDEBARMENU", true))
                    }
                    this.restoreFocus()
                }
            },
            deleteQuestion(val) {
                if (!val) {
                    if (!this.$store.state.sidebarMenu) {
                        this.$nextTick(() => this.$store.commit("SET_SIDEBARMENU", true))
                    }
                    this.restoreFocus()
                }
            },
            keepKeywordsDialog(val) {
                if (!val) {
                    if (!this.$store.state.sidebarMenu) {
                        this.$nextTick(() => this.$store.commit("SET_SIDEBARMENU", true))
                    }
                    this.restoreFocus()
                }
            },

            publish(val) {
                if (val === true) this.switchlbl = "publish"
                else this.switchlbl = "don’t publish"
            },
            "$store.state.open_save_collection_dialog": {
                // - TOUR
                immediate: true,
                handler(val) {
                    if (val) {
                        this.dialog = true
                        this.$store.dispatch("UNSET_TOUR_VARIABLES")
                    }
                }
            },
            "$store.state.set_collection_name": {
                // - TOUR
                immediate: true,
                handler(val) {
                    if (val) {
                        if (this.documentName.length == 0) {
                            this.documentName = "Sneaker - Test Collection"
                        }
                        this.$store.dispatch("UNSET_TOUR_VARIABLES")
                    }
                }
            },
            "$store.state.save_collection": {
                // - TOUR
                immediate: true,
                async handler(val) {
                    if (val) {
                        let created = await this.prepareSaveResearch(this.keywords)
                        console.log(created)
                        if (!created) {
                            let randomNumber = Math.floor(Math.random() * 100)
                            this.documentName = "Sneaker - Test Collection - " + randomNumber
                            console.log(this.documentName)
                            this.prepareSaveResearch(this.keywords)
                        }
                        this.$store.dispatch("UNSET_TOUR_VARIABLES")
                    }
                }
            },
            "$store.state.skip_keep_keywords": {
                // - TOUR
                immediate: true,
                handler(val) {
                    if (val) {
                        setTimeout(() => {
                            this.keepKeywordsDialog = false
                        }, 500)
                    }
                }
            }
        },

        methods: {
            focusFilterBtn() {
                const target = document.getElementById("filter-activator") // aus SearchSettings.vue
                if (target) target.focus()
            },
            captureFocus() {
                // Merkt das zuletzt fokussierte Element (Button, Icon-Button, etc.)
                this.lastFocusedEl = document.activeElement || null
            },
            restoreFocus() {
                // Stellt den Fokus wieder her (oder fällt auf den Clipboard-Activator zurück)
                this.$nextTick(() => {
                    if (this.lastFocusedEl && typeof this.lastFocusedEl.focus === "function") {
                        try {
                            this.lastFocusedEl.focus()
                        } catch (e) {}
                    } else {
                        const fallback = document.getElementById("settings-activator")
                        if (fallback && typeof fallback.focus === "function") fallback.focus()
                    }
                    this.lastFocusedEl = null
                })
            },
            shortenKeyword(keyword) {
                if (keyword.length > 15) {
                    let temp = keyword.slice(0, 15)
                    return temp + "…"
                } else {
                    return keyword
                }
            },
            download() {
                this.downloadDialog = false
                this.showDownloadProgress = true

                let keywords = []
                let headers = this.$store.state.clipboard_headers

                keywords = JSON.parse(JSON.stringify(this.keywords))

                console.log(keywords)
                try {
                    this.$matomo.trackEvent("Clipboard", "download", "Keywords length: " + keywords.length, "")
                } catch (error) {
                    console.log(error)
                    console.log("matomo blocked")
                }
                keywords.sort(function (a, b) {
                    return b.avgsv - a.avgsv
                })
                let fields = this.$helpers.deleteDownloadColumns(headers.modifiedHeaders)
                let foundHeaders = headers.modifiedHeaders.find((x) => x.custom == true)
                console.log(foundHeaders)
                if (foundHeaders) fields = this.$helpers.addCustoms(keywords, fields)
                console.log(fields)
                if (this.doctype.data == "json2xls") {
                    keywords = this.$helpers.transformDataForExcelDownload(keywords, headers.modifiedHeaders)
                }
                if (this.doctype.data == "json2csv") {
                    keywords = keywords.map((x) => {
                        x.cats = x.cats ? x.cats[0][x.cats[0].length - 1].category_name : null // only one cat for donwload
                        x.keyword_difficulty = x.keyword_difficulty ? x.keyword_difficulty.difficulty : null
                        if (x.serp_items) {
                            let serpString = ""
                            x.serp_items.map((y, i) => (serpString += y.value += i == x.serp_items.length - 1 ? "" : ","))
                            x.serp_items = serpString
                        } else {
                            x.serp_items = null
                        }
                        return x
                    })
                }
                this.$http
                    .post(
                        process.env.VUE_APP_APIURL + "/research/" + this.doctype.data,
                        { data: this.$helpers.deleteDownloadAttributes(keywords), columns: fields },
                        { headers: { Authorization: this.auth } }
                    )
                    .then((response) => {
                        window.location = process.env.VUE_APP_APIURL + "/" + response.body
                        this.showDownloadProgress = false
                        this.restoreFocus()
                    })
            },
            getIndexOfHeader(header, headers) {
                //  console.log(header)
                for (let index = 0; index < headers.length; index++) {
                    if (header.text == headers[index].text) {
                        return true
                    }
                }
                return false
            },
            deleteList() {
                this.keywords = []
                this.showDeleteMessage = true
                this.deleteQuestion = false

                //delete store clipboard headers
                this.$store.dispatch("SET_CLIPBOARD_HEADERS")
                // console.log(this.$store.state.clipboard_headers)s
            },
            test() {
                // console.log(localStorage.getItem('keywords'))
            },
            getTableHeight() {
                return 150
            },
            // prepareDeleteItem(item){
            //   this.selectedItem = item;
            //   this.deleteQuestionItem = true;
            // },
            deleteItem(item) {
                let kwIndex = this.keywords.findIndex((x) => x.keyword == item.keyword)
                // console.log(kwIndex)
                let kw = this.keywords
                kw.splice(kwIndex, 1)
                // localStorage.setItem("keywords", JSON.stringify(kw))
                // this.$store.dispatch("CHANGE_SIDEBARMENU")
                // this.$store.dispatch("CHANGE_SIDEBARMENU")
            },
            disableExistMessage: function () {
                this.existMessage = ""
            },
            checkIfExists: function () {
                return new Promise((resolve, reject) => {
                    if (this.documentName != "") {
                        this.$http
                            .post(this.apiUrl + "/research/namecheck", { name: this.documentName, company_id: localStorage.company_id }, { headers: { Authorization: this.auth } })
                            .then(function (response) {
                                if (response.body.error) {
                                    this.existMessage = "This name already exists. Please choose another one."
                                    this.documentName = ""
                                    resolve(false)
                                } else {
                                    resolve(true)
                                }
                            })
                    } else {
                        this.existMessage = "Please enter a collection name."
                        this.documentName = ""
                        resolve(false)
                    }
                })
            },
            checkCredits: function (kw_count) {
                return new Promise((resolve, reject) => {
                    this.$http
                        .post(this.apiUrl + "/research/creditsavailable", { company_id: localStorage.company_id, kw_count: kw_count }, { headers: { Authorization: this.auth } })
                        .then(function (response) {
                            console.log(response)
                            if (!response.body.credits_left) {
                                resolve(false)
                            } else {
                                resolve(true)
                            }
                        })
                })
            },
            getExistingLists() {
                this.$http
                    .post(
                        this.apiUrl + "/research/availablelists",
                        {
                            type: "research",
                            owner: localStorage.id,
                            company_id: localStorage.company_id
                        },
                        { headers: { Authorization: this.auth } }
                    )
                    .then(function (response) {
                        console.log(response.body)
                        this.existLists = response.body.sort((a, b) => a.listname.localeCompare(b.listname))
                    })
            },
            async prepareSaveResearch(keywords) {
                this.btndisabled = true
                if (/\s$/.test(this.documentName)) {
                    this.documentName = this.documentName.replace(/^[ ]+|[ ]+$/g, "")
                }
                let credits_left = await this.checkCredits(this.$helpers.removeDuplicatesByAttribute(keywords, "keyword").length)
                if (!credits_left) {
                    this.existMessage = "You don’t have enough credits left."
                    return false
                }
                var checkIfExists = await this.checkIfExists()
                if (checkIfExists) {
                    // check if name has no / & %
                    var validString = true
                    if (this.documentName.indexOf("%") > -1) validString = false
                    if (this.documentName.indexOf("/") > -1) validString = false
                    if (this.documentName.indexOf("&") > -1) validString = false
                    if (validString && this.documentName.length > 3) {
                        // //console.log("valid")
                        this.createResearch(keywords)
                        return true
                    } else {
                        this.btndisabled = false
                        this.existMessage = "Please use a proper name. Minimum 4 characters and none of [%, /, &]."
                        // this.documentName = ""
                        return false
                    }
                } else {
                    this.btndisabled = false
                    return false
                }
            },

            endSaveAndUpdate() {
                this.documentName = ""
                this.dialog = false
                this.$store.dispatch("CHANGE_SIDEBARMENU")
                // this.keywords = [];
                this.researchDialog = false
                this.keepKeywordsDialog = true
                this.btndisabled = false
            },

            async addToResearch() {
                this.btndisabled = true
                let credits_left = await this.checkCredits(this.$helpers.removeDuplicatesByAttribute(this.keywords, "keyword").length)
                if (!credits_left) {
                    this.createInfo("You don’t have any credits left. Please contact support via chat or email.", "#fcba03", "mdi-alert-circle-outline")
                    this.btndisabled = false
                    return false
                }
                this.$http
                    .post(
                        this.apiUrl + "/research/listdata",
                        {
                            listname: this.selectedList.listname,
                            company_id: localStorage.company_id
                        },
                        { headers: { Authorization: this.auth } }
                    )
                    .then((response) => {
                        console.log(response.body[0].count)
                        console.log(this.keywords.length)
                        console.log("new", response.body[0].mdata.langloc_new)
                        console.log("selected", this.selectedLocation)
                        console.log(response.body[0].mdata.langloc_new == this.selectedLocation)
                        if (!isCollectionBusy(response.body[0].mdata)) {
                            if (
                                response.body[0].mdata.langloc_new.value == this.selectedLocation.value &&
                                response.body[0].mdata.langloc_new.loc == this.selectedLocation.loc &&
                                response.body[0].mdata.langloc_new.ads_lang_code == this.selectedLocation.ads_lang_code &&
                                response.body[0].mdata.langloc_new.serp_lang_code == this.selectedLocation.serp_lang_code
                            ) {
                                if (response.body[0].count + this.keywords.length <= 10000) {
                                    let data = {
                                        keywords: this.$helpers.removeDuplicatesByAttribute(this.keywords, "keyword"),
                                        attributes: {
                                            name: this.selectedList.listname,
                                            user: localStorage.id,
                                            type: this.collectionType.data,
                                            shared: this.publish,
                                            editable: true,
                                            lang: this.selectedLocation.lang,
                                            loc: this.selectedLocation.value,
                                            value: this.selectedLocation.loc,
                                            headers: this.$store.state.clipboard_headers
                                        },
                                        company_id: localStorage.company_id,
                                        socket_id: this.$socket.id
                                    }
                                    this.$http
                                        .post(this.apiUrl + "/research/keywords/add", data, {
                                            headers: { Authorization: this.auth }
                                        })
                                        .then((response) => {
                                            if (response.status == 200) {
                                                this.endSaveAndUpdate()
                                            } else {
                                                this.addToResearch()
                                            }
                                        })
                                } else {
                                    this.createInfo("Collection limit is 10,000 keywords. Selected collection size is: " + response.body[0].count, "#fcba03", "mdi-alert-circle-outline")
                                    this.btndisabled = false
                                }
                            } else {
                                this.createInfo(
                                    "The keywords in your clipboard use search volume data for a different country. Please select a collection with the same country.",
                                    "#fcba03",
                                    "mdi-alert-circle-outline"
                                )
                                this.btndisabled = false
                            }
                        } else {
                            this.createInfo("This research is currently being updated. Please try again in a few moments.", "#fcba03", "mdi-alert-circle-outline")
                            this.btndisabled = false
                        }
                    })
            },
            createInfo(message, color, icon) {
                this.info.message = message
                this.info.color = color
                this.info.icon = icon
                this.showInfo = true
            },
            createResearch(keywords) {
                this.showAddMessage = true
                console.log(keywords)

                let data = {
                    keywords: this.$helpers.removeDuplicatesByAttribute(keywords, "keyword"),
                    attributes: {
                        name: this.documentName,
                        user: localStorage.id,
                        type: this.collectionType.data,
                        shared: this.publish,
                        editable: true,
                        lang: this.selectedLocation.lang,
                        loc: this.selectedLocation.value,
                        value: this.selectedLocation.loc,
                        langloc_new: this.selectedLocation,
                        headers: this.$store.state.clipboard_headers
                    },
                    company_id: localStorage.company_id,
                    socket_id: this.$socket.id
                }
                this.$http
                    .post(this.apiUrl + "/research/create", data, {
                        headers: { Authorization: this.auth }
                    })
                    .then((response) => {
                        // console.log(response.status)
                        if (response.status == 200) {
                            this.endSaveAndUpdate()
                        } else {
                            this.createResearch(keywords)
                        }
                    })
            }
        },
        mounted() {
            this.$socket.on("reloadAvailableLists", (data) => {
                console.log("Reloading avalable Lists")
                this.getExistingLists()
            })
        },
        updated() {
            // console.log(this.keywords)
        }
    }
</script>
<style scoped>
    .clipboard-trigger {
        margin-top: 0px !important;
        position: fixed;
        top: 115px;
        right: 0px;
        height: 52px !important;
        border-top-right-radius: 0px;
        border-bottom-right-radius: 0px;
        border-top-left-radius: 25px;
        border-bottom-left-radius: 25px;
        /* über allem: App-Bar, Drawer, Snackbars etc. */
        z-index: 9999;
        pointer-events: auto;
        /* Größe & Layout */
        width: 56px;
        min-width: 56px;
        display: inline-flex;
        align-items: center;
        justify-content: center;

        /* Optik (nicht rund) */
        background: var(--v-primary-base);
        color: #fff;
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
        cursor: pointer;

        /* Stabilität */
        line-height: 1;
        padding: 0;
        box-sizing: border-box;
    }
    .clipboard-trigger .v-icon {
        line-height: 1;
    }
    .clipboard-trigger:hover {
        filter: brightness(1.05);
    }
    .clipboard-trigger:active {
        transform: translateY(1px);
    }
    .clipboard-trigger:focus-visible {
        outline: 3px solid var(--v-primary-base);
        outline-offset: 2px;
    }
    .quaro-settings-fab {
        position: fixed !important;
        top: 115px;
        right: 0; /* im Viewport halten */
        transform: translateX(16px); /* optisch „ragt“ er rein, ohne Hitbox-Probleme */
        z-index: 4;
        opacity: 0.9;

        /* Verzug verhindern: feste Maße */
        width: 40px;
        height: 40px;
        min-width: 40px;
        line-height: 40px;
        padding: 0; /* kein zusätzliches Padding */
    }
    .quaro-settings-fab .v-icon {
        /* Icon mittig halten */
        line-height: 40px;
    }
    .sr-only {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 1px, 1px) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }
    .v-btn {
        margin-top: 10px;
    }
    .existMessage {
        color: red;
    }
    #maintitle {
        padding-top: 3px;
        line-height: 25px;
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        text-align: center !important;
        font-size: 1em;
    }
    #title {
        padding-top: 16px;
        line-height: 25px;
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        text-align: center !important;
        color: #212529;
        font-size: 1em;
    }
    #savebody {
        padding-top: 0%;
    }
    #kwpformflex {
        padding-top: 0%;
    }
    #heading {
        font-size: 1.2em;
    }
    .actionBtns {
        margin-top: -4px !important;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        flex-wrap: wrap;
    }
    #divider {
        border: 1px solid rgb(231, 231, 231);
        border-radius: 1px;
    }
    .btnpadding {
        margin-right: 50px;
    }
    #title2 {
        padding-top: 16px;
        line-height: 25px;
        font-size: 12px;
        margin-left: 30%;
        font-weight: 600;
        text-transform: uppercase;
        text-align: center !important;
        color: #212529;
        font-size: 1em;
    }
    #addtocollection {
        padding-top: 15px;
        padding-bottom: 15px;
    }
    .clipboard-card {
        border-radius: 14px !important;
        border: 1px solid #ebeff7;
        box-shadow: 0 14px 40px rgba(25, 42, 70, 0.14) !important;
        overflow: hidden;
    }
    .clipboard-card__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 14px 10px 14px;
        background: linear-gradient(180deg, #f7faff 0%, #ffffff 100%);
        border-bottom: 1px solid #edf2fb;
    }
    .clipboard-card__title-wrap {
        display: inline-flex;
        align-items: center;
        font-weight: 700;
        color: #1f2a44;
        letter-spacing: 0.2px;
    }
    .clipboard-card__count {
        font-weight: 700;
        min-width: 24px;
        justify-content: center;
    }
    .clipboard-empty-state {
        border-radius: 10px;
        margin-bottom: 2px;
    }
    .clipboard-table {
        border: 1px solid #edf2fb;
        border-radius: 10px;
        overflow: hidden;
    }
    .save-dialog-card {
        border-radius: 14px !important;
        border: 1px solid #ebeff7;
        box-shadow: 0 14px 40px rgba(25, 42, 70, 0.16) !important;
        overflow: hidden;
    }
    .save-dialog__header {
        padding: 14px 16px 10px 16px;
        background: linear-gradient(180deg, #f7faff 0%, #ffffff 100%);
        border-bottom: 1px solid #edf2fb;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
    .save-dialog__title-wrap {
        display: inline-flex;
        align-items: center;
        font-weight: 700;
        color: #1f2a44;
        letter-spacing: 0.2px;
    }
    .save-dialog__subtitle {
        font-size: 12px;
        color: #64748b;
        margin-left: 2px;
    }
    .save-dialog__body {
        padding-top: 12px !important;
        padding-bottom: 10px !important;
    }
    .save-dialog__actions {
        padding: 12px !important;
    }
    .save-dialog__save-btn {
        border-radius: 10px;
        font-weight: 700;
        letter-spacing: 0.2px;
        text-transform: none;
    }
    .save-dialog-card .existMessage {
        display: inline-block;
        min-height: 18px;
        font-size: 12px;
        margin-bottom: 2px;
    }
    .download-dialog-card {
        border-radius: 14px !important;
        border: 1px solid #ebeff7;
        box-shadow: 0 14px 40px rgba(25, 42, 70, 0.16) !important;
        overflow: hidden;
    }
    .download-dialog__header {
        padding: 14px 16px 10px 16px;
        background: linear-gradient(180deg, #f7faff 0%, #ffffff 100%);
        border-bottom: 1px solid #edf2fb;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
    .download-dialog__title-wrap {
        display: inline-flex;
        align-items: center;
        font-weight: 700;
        color: #1f2a44;
        letter-spacing: 0.2px;
    }
    .download-dialog__subtitle {
        font-size: 12px;
        color: #64748b;
        margin-left: 2px;
    }
    .download-dialog__body {
        padding-top: 12px !important;
        padding-bottom: 10px !important;
    }
    .download-dialog__select {
        margin-right: 0 !important;
    }
    .download-dialog__actions {
        padding: 12px !important;
    }
    .download-dialog__download-btn {
        border-radius: 10px;
        font-weight: 700;
        letter-spacing: 0.2px;
        text-transform: none;
    }
    .research-dialog-card {
        border-radius: 14px !important;
        border: 1px solid #ebeff7;
        box-shadow: 0 14px 40px rgba(25, 42, 70, 0.16) !important;
        overflow: hidden;
    }
    .research-dialog__header {
        padding: 14px 16px 10px 16px;
        background: linear-gradient(180deg, #f7faff 0%, #ffffff 100%);
        border-bottom: 1px solid #edf2fb;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
    .research-dialog__title-wrap {
        display: inline-flex;
        align-items: center;
        font-weight: 700;
        color: #1f2a44;
        letter-spacing: 0.2px;
    }
    .research-dialog__subtitle {
        font-size: 12px;
        color: #64748b;
        margin-left: 2px;
    }
    .research-dialog__body {
        padding-top: 12px !important;
        padding-bottom: 10px !important;
    }
    .research-dialog__select {
        padding-top: 8px !important;
        padding-bottom: 6px !important;
    }
    .research-dialog__actions {
        padding: 12px !important;
    }
    .research-dialog__add-btn {
        border-radius: 10px;
        font-weight: 700;
        letter-spacing: 0.2px;
        text-transform: none;
    }
    .download-label {
        font-weight: bold;
        padding-bottom: 5px;
        display: flex;
    }
    .v-menu__content {
        border-radius: 15px !important;
    }
</style>
<style lang="sass" scoped>

    .v-settings
      // neu: maximale Höhe relativ zur Fensterhöhe + Scrollbar
      max-height: calc(100vh - 140px)
      overflow-y: auto

      .v-item-group > *
        cursor: pointer

      &__item
        border-width: 3px
        border-style: solid
        border-color: transparent !important

        &--active
          border-color: #00cae3 !important

    .infotext
      text-align: left

    .v-data-footer__select
      display: none
</style>
